import util from 'util';

import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import { Label } from 'reactstrap';

import ActionableComponentsList from 'app/ActionableComponentsList/components/ActionableComponentsList';
import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import MostRecentComponents from 'app/MostRecentComponents/container';
import Results from 'app/Search/components/Results';
import { MODAL_MESSAGES } from 'lib/const';
import { EN } from 'lib/locales';
import { noop } from 'lib/noop';
import { LATEST } from 'lib/versions';
import { disabledComponents } from 'utils/factory';

import {
  COMPONENT_MENU_EDITOR_ACTIONS,
  COMPONENT_MENU_READER_ACTIONS,
  CONTEXT,
  ITEM_CLICK_ACTION,
  MENU_ACTION_DELETE,
  MENU_ACTION_DUPLICATE,
  MENU_ACTION_EDIT_ORIGINAL,
  MENU_ACTION_FAVORITE,
  MENU_ACTION_INSERT_DUPLICATE_INTO_CONTAINER,
  MENU_ACTION_INSERT_DUPLICATE_INTO_PAGE,
  MENU_ACTION_INSERT_INTO_PAGE,
  MENU_ACTION_UNFAVORITE,
  PAGE_ACTIONS_FAVORITE,
  PAGE_ACTIONS_UNFAVORITE,
  PAGE_COMPONENT_MENU_EDITOR_ACTIONS,
  PAGE_COMPONENT_MENU_READER_ACTIONS,
  PAGE_CONTAINER_MENU_EDITOR_ACTIONS,
  PAGE_CONTAINER_MENU_READER_ACTIONS
} from '../const';

/**
 * This component is the combination of two features:
 * 1. the component level Favorite Components List and Most Recent Components List
 * 2. the component AND page level results
 * @returns {React.Component} the component for a page search component.
 */
export default class ComponentSearch extends Component {
  static propTypes = {
    addDuplicateComponentToContainer: propTypes.func.isRequired,
    addDuplicateComponentToPage: propTypes.func.isRequired,
    addMasterComponentToPage: propTypes.func.isRequired,
    checkStatusAndVisitPage: propTypes.func,
    context: propTypes.string,
    createDuplicateComponent: propTypes.func.isRequired,
    createNewUnsavedPage: propTypes.func,
    currPageNum: propTypes.number,
    cwdDetails: propTypes.shape({
      Id: propTypes.string,
      Name: propTypes.string,
      Breadcrumb: propTypes.arrayOf(
        propTypes.shape({
          Id: propTypes.string,
          Name: propTypes.string
        })
      ),
      Children: propTypes.arrayOf(
        propTypes.shape({
          Id: propTypes.string,
          Name: propTypes.string,
          Type: propTypes.string
        })
      ),
      DateCreated: propTypes.string,
      DateModified: propTypes.string
    }),
    deleteComponent: propTypes.func.isRequired,
    deletePage: propTypes.func,
    duplicatePage: propTypes.func,
    favoriteComponents: propTypes.array.isRequired,
    favoritePages: propTypes.arrayOf(propTypes.shape()),
    getComponentAndExecuteFunction: propTypes.func.isRequired,
    hideLists: propTypes.bool,
    isContainer: propTypes.bool,
    isPageSelected: propTypes.bool,
    isResultsLoading: propTypes.bool,
    onFinish: propTypes.func,
    onResultClick: propTypes.func,
    openForEditing: propTypes.bool,
    pageNumStart: propTypes.number,
    resultActions: propTypes.arrayOf(
      propTypes.shape({
        text: propTypes.string.isRequired,
        action: propTypes.string.isRequired
      })
    ),
    results: propTypes.array,
    storeResultPageNum: propTypes.func,
    switchToComponent: propTypes.func.isRequired,
    toggleFavoriteContent: propTypes.func.isRequired
  };

  static defaultProps = {
    checkStatusAndVisitPage: noop,
    context: undefined,
    createNewUnsavedPage: noop,
    currPageNum: 1,
    cwdDetails: {},
    deletePage: noop,
    duplicatePage: noop,
    favoritePages: [],
    hideLists: false,
    isContainer: false,
    isPageSelected: false,
    isResultsLoading: false,
    onFinish: noop,
    onResultClick: undefined,
    openForEditing: false,
    pageNumStart: 1,
    resultActions: null,
    results: [],
    storeResultPageNum: noop
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedContent: {},
      showDeleteComponent: false
    };
    this.handleDeleteComponent = this.handleDeleteComponent.bind(this);
    this.toggleDeleteComponent = this.toggleDeleteComponent.bind(this);
  }

  /**
   * deletes the selected component
   */
  handleDeleteComponent() {
    const { selectedContent } = this.state;
    const { deleteComponent, onFinish } = this.props;
    deleteComponent(selectedContent.id, selectedContent.name);
    this.toggleDeleteComponent();
    onFinish();
  }

  /**
   * executers selected action on selected component.
   * @param {object} content - selected component object
   * @param {string} action - action taken on selected component
   * @param {string} context - context, page or component
   */
  onActionSelect = (content, action, context = 'component') => {
    const {
      addDuplicateComponentToContainer,
      addDuplicateComponentToPage,
      addMasterComponentToPage,
      checkStatusAndVisitPage,
      createDuplicateComponent,
      cwdDetails,
      deletePage,
      duplicatePage,
      getComponentAndExecuteFunction,
      isContainer,
      isPageSelected,
      onFinish,
      onResultClick,
      openForEditing,
      switchToComponent,
      toggleFavoriteContent
    } = this.props;

    if (action !== MENU_ACTION_DELETE.action) {
      onFinish();
    }
    if (context === CONTEXT.COMPONENT) {
      switch (action) {
        case MENU_ACTION_FAVORITE.action: {
          toggleFavoriteContent(
            false,
            content.ComponentName,
            CONTEXT.COMPONENT,
            content.Id,
            EN
          );
          break;
        }
        case MENU_ACTION_UNFAVORITE.action: {
          toggleFavoriteContent(
            true,
            content.ComponentName,
            CONTEXT.COMPONENT,
            content.Id,
            EN
          );
          break;
        }
        case MENU_ACTION_DUPLICATE.action: {
          createDuplicateComponent(content.Id);
          break;
        }
        case MENU_ACTION_DELETE.action: {
          this.setState({
            selectedContent: {
              id: content.Id,
              name: content.ComponentName
            },
            showDeleteComponent: true
          });
          break;
        }
        case MENU_ACTION_EDIT_ORIGINAL.action: {
          switchToComponent(content.Id, LATEST);
          break;
        }
        case MENU_ACTION_INSERT_DUPLICATE_INTO_PAGE.action: {
          addDuplicateComponentToPage(content.Id);
          break;
        }
        case MENU_ACTION_INSERT_INTO_PAGE.action: {
          addMasterComponentToPage({
            id: content.Id,
            openForEditing
          });
          onFinish();
          break;
        }
        case ITEM_CLICK_ACTION.action: {
          if (isPageSelected && !isContainer) {
            addMasterComponentToPage({ id: content.Id, openForEditing });
          } else {
            getComponentAndExecuteFunction(content, onResultClick);
          }
          onFinish();
          break;
        }
        case MENU_ACTION_INSERT_DUPLICATE_INTO_CONTAINER.action: {
          addDuplicateComponentToContainer(content, onResultClick);
          break;
        }
        default:
          break;
      }
    } else if (context === CONTEXT.PAGE_OR_FOLDER) {
      switch (action) {
        case MENU_ACTION_DUPLICATE.action: {
          duplicatePage(content.Id, content.Version);
          break;
        }
        case MENU_ACTION_UNFAVORITE.action: {
          toggleFavoriteContent(true, content.PageName, CONTEXT.PAGE, content.Id, EN);
          break;
        }
        case MENU_ACTION_FAVORITE.action: {
          toggleFavoriteContent(false, content.PageName, CONTEXT.PAGE, content.Id, EN);
          break;
        }
        case MENU_ACTION_DELETE.action: {
          this.setState(
            {
              selectedContent: {
                id: content.Id,
                name: content.PageName
              },
              showDeleteComponent: true
            },
            deletePage(content.Id, content.PageName, cwdDetails.Id),
            onFinish()
          );
          break;
        }
        case ITEM_CLICK_ACTION.action: {
          checkStatusAndVisitPage(content.Id, content.Version);
          break;
        }
        default:
          break;
      }
    }
  };

  /**
   * executes actions on click on any of the search result items
   * @param {object} result - selected result item
   * @param {string} context - result context: component or pageOrFolder
   */
  onSearchResultClick = (result, context) => {
    const { isPageSelected, onResultClick } = this.props;
    if (context === CONTEXT.PAGE_OR_FOLDER) {
      this.onActionSelect(result, ITEM_CLICK_ACTION.action, CONTEXT.PAGE_OR_FOLDER);
    } else if (onResultClick) {
      this.onActionSelect(result, ITEM_CLICK_ACTION.action, CONTEXT.COMPONENT);
    } else if (isPageSelected) {
      this.onActionSelect(result, MENU_ACTION_INSERT_INTO_PAGE.action, CONTEXT.COMPONENT);
    } else {
      this.onActionSelect(result, MENU_ACTION_EDIT_ORIGINAL.action, CONTEXT.COMPONENT);
    }
  };

  /**
   * returns array of actions allowed on component item
   * @param {object} content - item, either component or page
   * @param {string} context - either component or pageOrFolder
   * @param {boolean} isFavoriteSection - is the call coming from favorite section comps?
   * @returns {Array} returns array of actions allowed on search result item
   */
  getResultActions = (content, context, isFavoriteSection = false) => {
    const { favoritePages, isContainer = false, isPageSelected } = this.props;
    const favoritePagesIds = favoritePages.map(page => page.Id);
    if (context === CONTEXT.COMPONENT) {
      let componentActions;
      if (isContainer) {
        componentActions = content.isReadonly
          ? PAGE_CONTAINER_MENU_READER_ACTIONS
          : PAGE_CONTAINER_MENU_EDITOR_ACTIONS;
      } else if (isPageSelected) {
        componentActions = content.isReadonly
          ? PAGE_COMPONENT_MENU_READER_ACTIONS
          : PAGE_COMPONENT_MENU_EDITOR_ACTIONS;
      } else {
        componentActions = content.isReadonly
          ? COMPONENT_MENU_READER_ACTIONS
          : COMPONENT_MENU_EDITOR_ACTIONS;
      }
      return [
        ...this.getDisabledDeleteAction(content, componentActions),
        content.isFavorite || isFavoriteSection ? MENU_ACTION_UNFAVORITE : MENU_ACTION_FAVORITE
      ];
    }

    if (favoritePagesIds.includes(content.Id)) {
      return PAGE_ACTIONS_UNFAVORITE;
    }
    return PAGE_ACTIONS_FAVORITE;
  };

  /**
   * modify delete action for components
   * @param {object} component - component data from search/favorite/most recent sections
   * @param {*} actionList - menu actions
   * @returns {Array} modified actions
   */
  getDisabledDeleteAction(component, actionList) {
    let actions = actionList;
    if (component.IsDoNotArchive || (component.IsDynamic && component.Published)) {
      actions = actions.map(f =>
        f.action === MENU_ACTION_DELETE.action ? { ...f, disabled: true } : f
      );
    }
    return actions;
  }

  /**
   * toggles delete component modal
   */
  toggleDeleteComponent() {
    this.setState(ps => ({ showDeleteComponent: !ps.showDeleteComponent }));
  }

  /**
   * renders modal for delete component confirmation.
   * @returns {Component} component to show a modal.
   */
  renderDeleteComponentModal() {
    const { selectedContent, showDeleteComponent } = this.state;
    return (
      <ConfirmationModal
        headerText={util.format(
          MODAL_MESSAGES.CONFIRMATION_HEADER_MESSAGE,
          MODAL_MESSAGES.DELETE_LABEL,
          MODAL_MESSAGES.CONTENT_LABEL
        )}
        isOpen={showDeleteComponent}
        noButtonLabel={MODAL_MESSAGES.CANCEL_LABEL}
        onNoClick={this.toggleDeleteComponent}
        onYesClick={this.handleDeleteComponent}
        yesButtonLabel={MODAL_MESSAGES.DELETE_LABEL}
      >
        <div className="p-1">
          <Label>
            {util.format(
              MODAL_MESSAGES.DELETE_CONTENT_MESSAGE,
              MODAL_MESSAGES.CONTENT_LABEL,
              `"${selectedContent.name}"`
            )}
          </Label>
        </div>
      </ConfirmationModal>
    );
  }

  render() {
    const {
      context,
      createNewUnsavedPage,
      currPageNum,
      duplicatePage,
      favoriteComponents,
      hideLists,
      isContainer,
      isPageSelected,
      isResultsLoading,
      onFinish,
      pageNumStart,
      resultActions,
      results,
      storeResultPageNum
    } = this.props;
    const { showDeleteComponent } = this.state;

    const disabledFavoriteComponents = disabledComponents(
      favoriteComponents,
      isContainer,
      isPageSelected,
      true
    );

    return (
      <div className="w-100">
        {!hideLists && (
          <Fragment>
            <ActionableComponentsList
              components={sortBy(disabledFavoriteComponents, fc => fc.ComponentName)}
              context={context}
              getItemActions={this.getResultActions}
              headerText="Favorite Components"
              isRecentComponentSection
              onActionSelect={this.onActionSelect}
              onItemClick={this.onSearchResultClick}
            />
            <MostRecentComponents
              getComponentActions={this.getResultActions}
              isContainer={isContainer}
              isPage={isPageSelected}
              onActionSelect={this.onActionSelect}
              onItemClick={this.onSearchResultClick}
            />
          </Fragment>
        )}
        {hideLists && (
          <Results
            context={context}
            createNewUnsavedPage={createNewUnsavedPage}
            currPageNum={currPageNum}
            duplicatePage={duplicatePage}
            getResultActions={this.getResultActions}
            isResultsLoading={isResultsLoading}
            onActionSelect={this.onActionSelect}
            onFinish={onFinish}
            onResultClick={this.onSearchResultClick}
            pageNumStart={pageNumStart}
            resultActions={resultActions}
            results={results}
            storeResultPageNum={storeResultPageNum}
          />
        )}
        {showDeleteComponent && this.renderDeleteComponentModal()}
      </div>
    );
  }
}
